import React from 'react';
import { useTranslation } from 'react-i18next';
import { getTestsCount } from '../utils/testUtils';
import NodeForm from './NodeForm';
import PreviewNode from './PreviewNode';
import Spinner from './ui/spinner';
import apiClient from '../utils/apiClient';

/**
 * CreateNodeSection
 * Handles creation and edition of nodes, shows a preview and tests information.
 */
const CreateNodeSection = ({
  selectedNode,
  resetSelection,
  loading,
  setLoading,
  nodeName,
  setNodeName,
  nodeType,
  setNodeType,
  technology,
  setTechnology,
  featureTeam,        // 👈 NEW: Feature team field
  setFeatureTeam,     // 👈 NEW: Feature team setter
  status,
  setStatus,
  nodeDescription,
  setNodeDescription,
  metadata,
  setMetadata,
  endpointSchema,
  setEndpointSchema,
  saveNode,
  updateNodeSimple,
  generateTests,
  resetForm,
  setPreviewNode,
  setShowPreview,
  setPreviewError,
  previewNode,
  addPendingNode,
  nodes,
  API_BASE_URL,
  mode = 'libre',
  onValidate = undefined,
  onBack = undefined,
}) => {
  const { t } = useTranslation();

  const handleFetchTests = async (node) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/node/${node.name}/tests`);
      const data = response.data;
      // handle data if needed
    } catch (error) {
      console.error(t('createNode.errors.loadingTests'), error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="section-create-node">
      <div className="modern-node-editor-container">
        {/* Header */}
        <div className="node-editor-header">
          <div className="editor-summary-card">
            <div className="editor-indicator">
              <span className="editor-icon">{selectedNode ? '✏️' : '➕'}</span>
              <div className="editor-info">
                <h3 className="editor-title">
                  {selectedNode ? t('createNode.editSelectedNode') : t('createNode.createNewNode')}
                </h3>
                <p className="editor-description">
                  {selectedNode ? t('createNode.editDescription') : t('createNode.createDescription')}
                </p>
              </div>
            </div>

            <div className="editor-status">
              <div className="mode-indicator">
                <span className="mode-dot"></span>
                <span className="mode-text">
                  {t('createNode.modeLabel')}: {selectedNode ? t('createNode.mode.edit') : t('createNode.mode.create')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="node-editor-body">
          {/* Selected node preview */}
          {selectedNode && (
            <div className="selected-node-preview">
              <div className="preview-header">
                <h4 className="preview-title">{t('createNode.currentlySelectedNodeTitle')}</h4>
                <button
                  className="deselect-btn"
                  onClick={resetSelection}
                  disabled={loading}
                  title={t('createNode.deselectTitle')}
                >
                  {loading ? <Spinner /> : t('createNode.deselect')}
                </button>
              </div>

              <div className="preview-content">
                <div className="preview-main-info">
                  <div className="info-row">
                    <div className="info-item">
                      <span className="info-icon">🏷️</span>
                      <div className="info-details">
                        <span className="info-label">{t('createNode.label.name')}</span>
                        <span className="info-value">{selectedNode.name}</span>
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-icon">🔧</span>
                      <div className="info-details">
                        <span className="info-label">{t('createNode.label.type')}</span>
                        <span className="info-value">{selectedNode.type}</span>
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-icon">⚙️</span>
                      <div className="info-details">
                        <span className="info-label">{t('createNode.label.technology')}</span>
                        <span className="info-value">{selectedNode.technology}</span>
                      </div>
                    </div>
                  </div>

                  <div className="description-preview">
                    <span className="desc-icon">📝</span>
                    <div className="desc-content">
                      <span className="desc-label">{t('createNode.label.description')}</span>
                      <p className="desc-text">{selectedNode.description}</p>
                    </div>
                  </div>

                  <div className="tests-preview">
                    <span className="tests-icon">🧪</span>
                    <div className="tests-content">
                      <span className="tests-label">{t('createNode.label.tests')}</span>

                      {getTestsCount(selectedNode.testsJson) > 0 ? (
                        <div className="tests-info-positive">
                          <a
                            href="#section-tests"
                            onClick={() => handleFetchTests(selectedNode)}
                            className="tests-link"
                          >
                            {t('createNode.testsAvailable', { count: getTestsCount(selectedNode.testsJson) })}
                          </a>
                        </div>
                      ) : (
                        <div className="tests-info-negative">
                          <span className="no-tests-text">{t('createNode.noTests')}</span>
                          <button onClick={generateTests} disabled={loading} className="generate-tests-btn">
                            {t('createNode.generate')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata & schema accordion */}
              <div className="metadata-accordion">
                <details className="metadata-details">
                  <summary className="metadata-summary">
                    <span className="summary-icon">🗂️</span>
                    <span className="summary-text">{t('createNode.metadataTitle')}</span>
                    <span className="summary-arrow">▼</span>
                  </summary>
                  <div className="metadata-content">
                    <pre className="metadata-json-preview">{JSON.stringify(selectedNode.metadata, null, 2)}</pre>
                  </div>
                </details>

                <details className="schema-details">
                  <summary className="schema-summary">
                    <span className="summary-icon">🌐</span>
                    <span className="summary-text">{t('createNode.endpointSchemaTitle')}</span>
                    <span className="summary-arrow">▼</span>
                  </summary>
                  <div className="schema-content">
                    <pre className="schema-json-preview">{JSON.stringify(selectedNode.endpointSchema, null, 2)}</pre>
                  </div>
                </details>
              </div>
            </div>
          )}

          {/* Form section */}
          <div className="node-form-section">
            <div className="form-header">
              <h4 className="form-title">{selectedNode ? t('createNode.editProperties') : t('createNode.newNodeProperties')}</h4>
              <div className="form-actions-mini">
                {!selectedNode && resetForm && (
                  <button
                    type="button"
                    className="clear-form-btn"
                    onClick={resetForm}
                    disabled={loading}
                  >
                    <span className="clear-icon">🧹</span>
                    <span>{t('clearFormTitle')}</span>
                  </button>
                )}
                {selectedNode && (
                  <button className="reset-form-btn" onClick={resetForm} disabled={loading} title={t('createNode.resetFormTitle')}>
                    🔄 {t('createNode.reset')}
                  </button>
                )}
              </div>
            </div>

            <NodeForm
              selectedNode={selectedNode}
              nodeName={nodeName}
              setNodeName={setNodeName}
              nodeType={nodeType}
              setNodeType={setNodeType}
              technology={technology}
              setTechnology={setTechnology}
              featureTeam={featureTeam}           // 👈 NEW: Pass featureTeam
              setFeatureTeam={setFeatureTeam}     // 👈 NEW: Pass setFeatureTeam
              status={status}
              setStatus={setStatus}
              nodeDescription={nodeDescription}
              setNodeDescription={setNodeDescription}
              metadata={metadata}
              setMetadata={setMetadata}
              endpointSchema={endpointSchema}
              setEndpointSchema={setEndpointSchema}
              loading={loading}
              saveNode={saveNode}
              updateNodeSimple={updateNodeSimple}
              resetForm={resetForm}
              setPreviewNode={setPreviewNode}
              setShowPreview={setShowPreview}
              setPreviewError={setPreviewError}
              addPendingNode={addPendingNode}     // 👈 NOUVEAU : Pass addPendingNode
            />

            <div className="flex justify-center w-full">
              <PreviewNode
                previewNode={previewNode}
                setShowPreview={setShowPreview}
                setPreviewNode={setPreviewNode}
                resetForm={resetForm}
                addPendingNode={addPendingNode}
                nodes={nodes}
                API_BASE_URL={API_BASE_URL}
                setLoading={setLoading}
                mode={typeof mode !== 'undefined' ? mode : 'libre'}
                onValidate={onValidate}
                onBack={onBack}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateNodeSection;